import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { STAGES, getStage } from "../data/stages";
import PageHeader from "../components/PageHeader";
import CompanyLogo from "../components/CompanyLogo";
import StageSelect from "../components/StageSelect";
import EmptyState from "../components/EmptyState";
import { formatDate, timeAgo } from "../utils/format";
import { nextStep, stepProgress } from "../utils/jobs";

function NextStepHint({ job }) {
  const step = nextStep(job);
  if (!step) return null;
  return (
    <span className="next-step-hint">
      <i className={`bi ${step.icon}`} /> {step.label}
    </span>
  );
}

function BoardCard({ job, onDragStart }) {
  const navigate = useNavigate();
  const { done, total } = stepProgress(job);
  return (
    <div
      className="board-card"
      draggable
      onDragStart={(e) => onDragStart(e, job.id)}
      onClick={() => navigate(`/jobs/${job.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/jobs/${job.id}`)}
    >
      <div className="d-flex gap-2 align-items-start">
        <CompanyLogo name={job.company} size={32} />
        <div className="min-w-0 flex-grow-1">
          <div className="fw-medium small text-truncate">{job.title}</div>
          <div className="text-muted text-truncate" style={{ fontSize: 12 }}>
            {job.company}
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-2 gap-2">
        <NextStepHint job={job} />
        <span
          className="text-muted ms-auto flex-shrink-0"
          style={{ fontSize: 11 }}
          title={`${done} of ${total} steps done`}
        >
          {done}/{total}
        </span>
      </div>
    </div>
  );
}

function Board({ jobs }) {
  const { moveJob, toast } = useApp();
  const [dragOver, setDragOver] = useState(null);

  const onDragStart = (e, id) => e.dataTransfer.setData("text/plain", id);
  const onDrop = async (e, stageId) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const job = jobs.find((j) => j.id === id);
    if (job && job.stage !== stageId && (await moveJob(id, stageId))) {
      toast(`${job.company} moved to ${getStage(stageId).label}`);
    }
  };

  return (
    <div className="board">
      {STAGES.map((stage) => {
        const items = jobs.filter((j) => j.stage === stage.id);
        return (
          <div
            key={stage.id}
            className={`board-col ${dragOver === stage.id ? "drag-over" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(stage.id);
            }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => onDrop(e, stage.id)}
          >
            <div className="board-col-header" title={stage.description}>
              <span className="dot" style={{ background: stage.color }} />
              <span className="fw-semibold small">{stage.label}</span>
              <span className="board-count">{items.length}</span>
            </div>
            <div className="d-flex flex-column gap-2">
              {items.map((job) => (
                <BoardCard key={job.id} job={job} onDragStart={onDragStart} />
              ))}
              {items.length === 0 && (
                <div className="board-empty">Drag jobs here</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function List({ jobs }) {
  const navigate = useNavigate();
  if (jobs.length === 0) {
    return (
      <div className="card">
        <EmptyState
          icon="bi-funnel"
          title="No jobs here"
          text="Try another stage or clear your search."
        />
      </div>
    );
  }
  return (
    <div className="card">
      <div className="list-flush">
        <div className="list-head d-none d-md-grid">
          <span>Job</span>
          <span>Stage</span>
          <span>Next step</span>
          <span>Applied</span>
        </div>
        {jobs.map((job) => (
          <div
            key={job.id}
            className="list-row"
            onClick={() => navigate(`/jobs/${job.id}`)}
          >
            <div className="d-flex align-items-center gap-3 min-w-0">
              <CompanyLogo name={job.company} size={36} />
              <div className="min-w-0">
                <Link
                  to={`/jobs/${job.id}`}
                  className="fw-medium text-truncate d-block text-body text-decoration-none"
                >
                  {job.title}
                </Link>
                <div className="small text-muted text-truncate">
                  {job.company} · {job.location}
                </div>
              </div>
            </div>
            <div>
              <StageSelect job={job} />
            </div>
            <div className="d-none d-md-block small">
              <NextStepHint job={job} />
            </div>
            <div className="d-none d-md-block small text-muted">
              {job.appliedAt ? formatDate(job.appliedAt) : "-"}
              <div style={{ fontSize: 11 }}>
                Updated {timeAgo(job.updatedAt || job.createdAt)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tracker() {
  const { jobs, openAddJob, loadSampleData } = useApp();
  const [params, setParams] = useSearchParams();
  const stageFilter = params.get("stage") || "";
  const [view, setView] = useState(() =>
    stageFilter || window.innerWidth < 992 ? "list" : "board",
  );
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const searched = jobs.filter(
    (j) =>
      !q || `${j.title} ${j.company} ${j.location}`.toLowerCase().includes(q),
  );
  const listJobs = searched.filter(
    (j) => !stageFilter || j.stage === stageFilter,
  );

  const setStage = (id) => {
    if (id) setParams({ stage: id });
    else setParams({});
    setView("list");
  };

  return (
    <>
      <PageHeader
        title="Tracker"
        subtitle="Every job you're pursuing, grouped by where it stands."
        actions={
          <button className="btn btn-primary" onClick={() => openAddJob()}>
            <i className="bi bi-plus-lg me-1" /> Add job
          </button>
        }
      />

      {jobs.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="bi-kanban"
            title="Your tracker is empty"
            text="Save a job from search or paste a link to start tracking."
            action={
              <div className="d-flex gap-2 justify-content-center">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => openAddJob()}
                >
                  Paste a link
                </button>
                <Link to="/find" className="btn btn-light btn-sm">
                  Find jobs
                </Link>
                <button
                  className="btn btn-link btn-sm text-muted"
                  onClick={loadSampleData}
                >
                  Load sample jobs
                </button>
              </div>
            }
          />
        </div>
      ) : (
        <>
          <div className="d-flex flex-column flex-md-row gap-2 mb-3">
            <div className="input-group tracker-search">
              <span className="input-group-text bg-white">
                <i className="bi bi-search" />
              </span>
              <input
                className="form-control"
                placeholder="Search your jobs"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search your jobs"
              />
            </div>
            <div
              className="btn-group ms-md-auto"
              role="group"
              aria-label="View"
            >
              <button
                className={`btn btn-outline-secondary ${view === "board" ? "active" : ""}`}
                onClick={() => setView("board")}
              >
                <i className="bi bi-kanban me-1" /> Board
              </button>
              <button
                className={`btn btn-outline-secondary ${view === "list" ? "active" : ""}`}
                onClick={() => setView("list")}
              >
                <i className="bi bi-list-ul me-1" /> List
              </button>
            </div>
          </div>

          {view === "list" && (
            <div className="chip-scroll mb-3">
              <button
                className={`chip ${!stageFilter ? "active" : ""}`}
                onClick={() => setStage("")}
              >
                All <span className="chip-count">{searched.length}</span>
              </button>
              {STAGES.map((s) => (
                <button
                  key={s.id}
                  className={`chip ${stageFilter === s.id ? "active" : ""}`}
                  onClick={() => setStage(s.id)}
                >
                  <span className="dot" style={{ background: s.color }} />
                  {s.label}{" "}
                  <span className="chip-count">
                    {searched.filter((j) => j.stage === s.id).length}
                  </span>
                </button>
              ))}
            </div>
          )}

          {view === "board" ? (
            <>
              <p className="small text-muted d-none d-lg-block">
                <i className="bi bi-arrows-move me-1" /> Drag cards between
                columns to update their stage. Click a card to open it.
              </p>
              <Board jobs={searched} />
            </>
          ) : (
            <List jobs={listJobs} />
          )}
        </>
      )}
    </>
  );
}

export default Tracker;
